import { useEffect, useRef, useState } from 'react'
import { applyTemplate, fetchTemplates, type TemplateSummary } from '@/api/templates'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'

interface Props {
  onApplied: () => void
  onToast: (msg: string, kind?: 'ok' | 'err' | '') => void
  embedded?: boolean
}

export function TemplateGallery({ onApplied, onToast, embedded }: Props) {
  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState<string | null>(null)
  const toastRef = useRef(onToast)
  toastRef.current = onToast

  useEffect(() => {
    let cancelled = false
    void fetchTemplates()
      .then((r) => {
        if (!cancelled) setTemplates(r.templates || [])
      })
      .catch((e) => toastRef.current(String(e.message || e), 'err'))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const onApply = async (id: string) => {
    setApplying(id)
    toastRef.current('Seeding template…')
    try {
      const res = await applyTemplate(id)
      toastRef.current(
        `Template applied · ${res.accepted} events · ${res.patternsDetected} patterns`,
        'ok'
      )
      onApplied()
    } catch (e) {
      toastRef.current(String((e as Error).message || e), 'err')
    } finally {
      setApplying(null)
    }
  }

  const body = (
    <>
      {!embedded ? (
        <div className="section-head">
          <div>
            <h2>Template gallery</h2>
            <p>Enterprise starters — Workday, ServiceNow, Salesforce, and more.</p>
          </div>
        </div>
      ) : null}
      {loading ? (
        <div className="row-meta">Loading templates…</div>
      ) : !templates.length ? (
        <EmptyState title="No templates" body="Backend template catalog is empty." />
      ) : (
        <div className="stack">
          {templates.map((t) => (
            <article className="row" key={t.id}>
              <div className="row-top">
                <div>
                  <div className="row-title">{t.name}</div>
                  <div className="row-meta">{t.description}</div>
                  <div className="row-meta">
                    {t.domain} · {t.sessions} sessions · {t.stepCount} steps
                  </div>
                </div>
                <div className="pills">
                  <span
                    className={`pill ${t.riskExpectation === 'high' ? 'r' : t.riskExpectation === 'medium' ? 'a' : 'g'}`}
                  >
                    {t.riskExpectation}
                  </span>
                  {t.expectForbiddenHint ? <span className="pill r">policy</span> : null}
                </div>
              </div>
              <div className="row-actions">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={applying === t.id}
                  onClick={() => void onApply(t.id)}
                >
                  {applying === t.id ? 'Applying…' : 'Apply template'}
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  )

  if (embedded) return <div className="embed-panel">{body}</div>
  return (
    <section className="block" id="templates">
      {body}
    </section>
  )
}
